/**
 * View: Dashboard
 * Main dashboard view component
 */

import { useEffect, useState } from "react";
import type { FetchDashboardModel } from "../../domain/model/DashboardModel";
import { DashboardViewModel } from "../viewmodel/DashboardViewModel";
import { MainLayout } from "@/components/layout/MainLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import { SummaryCardsGroup } from "./components/SummaryCardsGroup/SummaryCardsGroup";
import { UserTierOverview } from "./components/UserTierOverview/UserTierOverview";
import { TransactionActivityChart } from "./components/TransactionActivityChart/TransactionActivityChart";
import { MerchantPointsTable } from "./components/MerchantPointsTable/MerchantPointsTable";
import { TopTransactionsCityTable } from "./components/TopTransactionsCityTable/TopTransactionsCityTable";
import { UserGenderStatistic } from "./components/UserGenderStatistic/UserGenderStatistic";
import { UserAgeStatistic } from "./components/UserAgeStatistic/UserAgeStatistic";
import { ReportDetailTable } from "./components/ReportDetailTable/ReportDetailTable";
import {
  ReportDetailActiveIcon,
  ReportDetailIcon,
  ReportSummaryActiveIcon,
  ReportSummaryIcon,
} from "@/assets/svg";

export const DashboardView = (): React.JSX.Element => {
  usePageTitle("Dashboard");
  
  const [dashboardData, setDashboardData] =
    useState<FetchDashboardModel | null>(null);
  const [isFetchingDashboard, setFetchingDashboard] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<"summary" | "detail">("summary");

  const { fetchDashboard } = DashboardViewModel({
    setFetchingDashboard,
    setDashboardData,
  });

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainLayout title="Dashboard Monthly Reporting">
      {isFetchingDashboard ? (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4 text-gray-800">
          <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
          <p>Loading dashboard...</p>
        </div>
      ) : !dashboardData ? (
        <div className="flex items-center justify-center min-h-screen text-red-500 text-lg">
          <p>Failed to load dashboard data</p>
        </div>
      ) : (
        <div>
        <SummaryCardsGroup summary={dashboardData.summary} />
        <div className="flex justify-between items-center mb-4 gap-4 max-md:flex-col max-md:items-stretch">
          <div className="flex border border-gray-200 rounded-[9px] p-[1px] gap-[1px]">
            <button
              className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-all duration-200 whitespace-nowrap font-medium text-sm leading-5 ${
                activeTab === "summary" ? "text-white bg-gradient-to-br from-[#001a41] to-[#0e336c]" : "bg-transparent text-[#001a41]"
              }`}
              onClick={() => setActiveTab("summary")}
            >
              {activeTab === "summary" ? (
                <ReportSummaryActiveIcon />
              ) : (
                <ReportSummaryIcon />
              )}
              Report Summary
            </button>
            <button
              className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-all duration-200 whitespace-nowrap font-medium text-sm leading-5 ${
                activeTab === "detail" ? "text-white bg-gradient-to-br from-[#001a41] to-[#0e336c]" : "bg-transparent text-[#001a41]"
              }`}
              onClick={() => setActiveTab("detail")}
            >
              {activeTab === "detail" ? (
                <ReportDetailActiveIcon />
              ) : (
                <ReportDetailIcon />
              )}
              Report Detail
            </button>
          </div>
        </div>

        {/* Conditional content based on active tab */}
        {activeTab === "summary" ? (
          <>
            <div className="grid grid-cols-[1fr_3fr] gap-4 mb-4 max-xl:grid-cols-1">
              <UserTierOverview data={dashboardData.userTiers} />
              <TransactionActivityChart data={dashboardData.transactionActivity} />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4 max-xl:grid-cols-1">
              <MerchantPointsTable data={dashboardData.merchantPoints} />
              <TopTransactionsCityTable data={dashboardData.cityTransactions} />
            </div>

            <div className="grid grid-cols-[1fr_3fr] gap-4 mb-6 max-xl:grid-cols-1">
              <UserGenderStatistic data={dashboardData.genderDemographics} />
              <UserAgeStatistic data={dashboardData.ageDemographics} />
            </div>
          </>
        ) : (
          <ReportDetailTable data={dashboardData.reportDetail} />
        )}
      </div>
      )}
    </MainLayout>
  );
};
